import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Web2 mirror of `apps/mobile/lib/parent/parentLinks.ts`.
 *
 * Reads `parent_children` for the current parent and joins with the
 * child user row to surface name + age band + club. RLS scopes the
 * read to `parent_id = auth.uid()` (per migration 00028). Errors fall
 * back to an empty list so the parent shell can render with a "no
 * children linked yet" empty state.
 *
 * The mobile helper returns LinkedChild[]. We match that shape so a
 * future shared @4cm/core helper can subsume both.
 */

export interface LinkedChild {
  id: string;
  name: string;
  ageBand: string | null;
  clubName: string | null;
  teamName: string | null;
  /** Parent-children link id, used for unlink / pause-link operations later. */
  linkId: string;
}

interface State {
  data: LinkedChild[];
  loading: boolean;
  error: string | null;
}

export function useLinkedChildren(version = 0): State & { refetch: () => void } {
  const { user, bypass } = useAuth();
  const [state, setState] = useState<State>({ data: [], loading: true, error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!user || bypass) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    (async () => {
      // Two-step: parent_children for the link rows, then users for the
      // child profile fields. Supabase's foreign-table select syntax
      // could fold this into one query, but two-step is more portable
      // across the schema variants we may encounter.
      const { data: links, error: linkErr } = await supabase
        .from('parent_children')
        .select('id, player_id')
        .eq('parent_id', user.id);
      if (cancelled) return;
      if (linkErr) {
        setState({ data: [], loading: false, error: linkErr.message });
        return;
      }
      if (!links || links.length === 0) {
        setState({ data: [], loading: false, error: null });
        return;
      }

      const playerIds = links.map((l) => l.player_id as string);
      const { data: kids, error: kidErr } = await supabase
        .from('users')
        .select('id, name, age_band, dob, club_name, team_name')
        .in('id', playerIds);
      if (cancelled) return;
      if (kidErr) {
        setState({ data: [], loading: false, error: kidErr.message });
        return;
      }

      const linkById = new Map(links.map((l) => [l.player_id as string, l.id as string]));
      const merged: LinkedChild[] = (kids ?? []).map((k) => ({
        id: k.id as string,
        name: (k.name as string | null) ?? 'Player',
        ageBand: (k.age_band as string | null) ?? null,
        clubName: (k.club_name as string | null) ?? null,
        teamName: (k.team_name as string | null) ?? null,
        linkId: linkById.get(k.id as string) ?? '',
      }));
      setState({ data: merged, loading: false, error: null });
    })();
    return () => {
      cancelled = true;
    };
  }, [user, bypass, version, tick]);

  return { ...state, refetch: () => setTick((t) => t + 1) };
}
